import Datatable from "../../EntryFile/datatable"; // Use custom Datatable
import Swal from "sweetalert2";
import { EyeIcon, PlusIcon } from "../../EntryFile/imagePath";
import { Link } from "react-router-dom";
import { Modal, Form, Spinner } from "react-bootstrap";
import alertify from "alertifyjs";
import { DeleteIcon, EditIcon } from "../../EntryFile/imagePath";
import Tabletop from "../../EntryFile/tabletop";
import { X } from "lucide-react";
import { getPatients, createPatient, updatePatient, deletePatient } from "../../core/api/patient";
import { useEffect, useState } from "react";

const PatientsCRUD = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPatientModal, setPatientModal] = useState(false);
  const [showEditPatientModal, setShowEditPatientModal] = useState(false);
  const [patientForm, setPatientForm] = useState({
    title: "",
    firstName: "",
    surname: "",
    idOrPassport: "",
    phoneNumber: "",
    alternateNumber: "",
    email: "",
    dob: "",
    gender: "",
    nextOfKin: "",
    postalAddress: "",
    physicalAddress: "",
  });
  const [editPatientId, setEditPatientId] = useState("");
  const [errors, setErrors] = useState({});
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState(null);
  const [pageSize] = useState(10);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Fetch patients from the API
  const fetchData = async ({ page, pageSize }) => {
    setLoading(true);
    try {
      const result = await getPatients({ page, limit: pageSize, search: search || "" });
      setData(result?.data?.patients);
      setTotalItems(result?.data?.totalPatients);
    } catch (error) {
      console.error(error);
      alertify.error("Failed to fetch patients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (search === null) return;
    fetchData({ page: 1, pageSize });
  }, [search]);

  // Handle patient form input change
  const handleValueChange = (e) => {
    const { name, value } = e.target;
    setPatientForm((prevForm) => ({ ...prevForm, [name]: value }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  // Handle patient form submit
  const handlePatientSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    const requiredFields = [
      "firstName",
    ];
    const newErrors = {};
    let hasErrors = false;

    requiredFields.forEach((field) => {
      if (!patientForm[field]) {
        newErrors[field] = "This field is required";
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    try {
      if (editPatientId) {
        await updatePatient(editPatientId, patientForm);
        alertify.success("Patient updated successfully!");
      } else {
        await createPatient(patientForm);
        alertify.success("Patient added successfully!");
      }
      fetchData({ page: 1, pageSize }); // Refresh the patient list
      setPatientModal(false); // Close the modal
      setShowEditPatientModal(false); // Close the modal
      resetForm(); // Reset the form
    } catch (err) {
      alertify.error(err?.response?.data?.message);
    }
  };

  // Reset the patient form
  const resetForm = () => {
    setPatientForm({
      title: "",
      firstName: "",
      surname: "",
      idOrPassport: "",
      phoneNumber: "",
      alternateNumber: "",
      email: "",
      dob: "",
      gender: "",
      nextOfKin: "",
      postalAddress: "",
      physicalAddress: "",
    });
    setErrors({});
  };

  // Handle patient deletion
  const handleDeletePatient = async (patientId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deletePatient(patientId);
          Swal.fire("Deleted!", "The patient has been deleted.", "success");
          fetchData({ page: 1, pageSize }); // Refresh the list after deletion
        } catch (error) {
          alertify.error("Failed to delete patient");
        }
      }
    });
  };

  useEffect(() => {
    fetchData({ page: 1, pageSize });
  }, []);

  // Define columns for the patients table
  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      render: (key) => <span>{key || '-'}</span>,
    },
    {
      title: "First Name",
      dataIndex: "firstName",
      render: (key) => <span>{key || '-'}</span>,
      sorter: (a, b) => a.firstName.localeCompare(b.firstName),
    },
    {
      title: "Surname",
      dataIndex: "surname",
      render: (key) => <span>{key || '-'}</span>,
      sorter: (a, b) => a.surname.localeCompare(b.surname),
    },
    {
      title: "ID/Passport",
      dataIndex: "idOrPassport",
      render: (key) => <span>{key || '-'}</span>,
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      render: (key) => <span>{key || '-'}</span>,
    },
    {
      title: "Alternate Number",
      dataIndex: "alternateNumber",
      render: (key) => <span>{key || '-'}</span>,
    },
    {
      title: "Email",
      dataIndex: "email",
      render: (key) => <span>{key || '-'}</span>,
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: "Next of Kin",
      dataIndex: "nextOfKin",
      render: (key) => <span>{key || '-'}</span>,
    },
    {
      title: "Actions",
      render: (record) => (
        <>
          <Link className="me-3" to="#" onClick={() => {
            setSelectedPatient(record);
            setShowDetailsModal(true);
          }}>
            <img src={EyeIcon} alt="View" />
          </Link>
          <Link className="me-3" to="#">
            <img
              src={EditIcon}
              alt="Edit"
              onClick={() => {
                setEditPatientId(record?.id);
                setPatientForm({
                  title: record?.title,
                  firstName: record?.firstName,
                  surname: record?.surname,
                  idOrPassport: record?.idOrPassport,
                  phoneNumber: record?.phoneNumber,
                  alternateNumber: record?.alternateNumber,
                  email: record?.email,
                  dob: record?.dob ? new Date(record?.dob).toISOString().split("T")[0] : "",
                  gender: record?.gender,
                  nextOfKin: record?.nextOfKin,
                  postalAddress: record?.postalAddress,
                  physicalAddress: record?.physicalAddress,
                });
                setShowEditPatientModal(true);
              }}
            />
          </Link>
          <Link className="confirm-text" to="#">
            <img
              src={DeleteIcon}
              alt="Delete"
              onClick={() => handleDeletePatient(record.id)}
            />
          </Link>
        </>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="page-title">
              <h4>Patients List</h4>
              <h6>Manage your patients</h6>
            </div>
            <div className="page-btn">
              <Link
                to="#"
                className="btn btn-added"
                onClick={() => {
                  setPatientModal(true);
                  resetForm();
                }}
              >
                <img src={PlusIcon} alt="img" className="me-1" />
                Add Patient
              </Link>
            </div>
          </div>

          {/* Patients Table */}
          <div className="card">
            <div className="card-body">
              <div className="row">
                <Tabletop OnChange={(e) => setSearch(e.target.value)} value={search} />
              </div>
              <div className="table-responsive">
                <Datatable
                  isLoading={loading}
                  columns={columns}
                  response={{
                    data: data?.map((item, index) => ({ ...item })),
                    totalDataCount: totalItems,
                  }}
                  fetchData={fetchData}
                  NoPagination={false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Patient Modal */}
      {showPatientModal && (
        <Modal show={showPatientModal} onHide={() => setPatientModal(false)} centered>
          <Modal.Header>
            <Modal.Title>Add Patient</Modal.Title>
            <button
              type="button"
              className="btn-close me-1"
              onClick={() => setPatientModal(false)}
            >
              <X size={20} onClick={() => setPatientModal(false)} />
            </button>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handlePatientSubmit}>
              <Form.Group className="form-group">
                <Form.Label>Title</Form.Label>
                <Form.Select
                  name="title"
                  value={patientForm.title}
                  onChange={handleValueChange}
                  className={`form-control ${errors.title ? "is-invalid" : ""}`}
                >
                  <option value="">Select Title</option>
                  <option value="Mr">Mr</option>
                  <option value="Mrs">Mrs</option>
                  <option value="Miss">Miss</option>
                  <option value="Ms">Ms</option>
                </Form.Select>
                {errors.title && <div className="invalid-feedback">{errors.title}</div>}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type="text"
                  name="firstName"
                  value={patientForm.firstName}
                  onChange={handleValueChange}
                  className={`form-control ${errors.firstName ? "is-invalid" : ""}`}
                />
                {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Surname</Form.Label>
                <Form.Control
                  type="text"
                  name="surname"
                  value={patientForm.surname}
                  onChange={handleValueChange}
                  className={`form-control ${errors.surname ? "is-invalid" : ""}`}
                />
                {errors.surname && <div className="invalid-feedback">{errors.surname}</div>}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>ID/Passport</Form.Label>
                <Form.Control
                  type="text"
                  name="idOrPassport"
                  value={patientForm.idOrPassport}
                  onChange={handleValueChange}
                  className={`form-control ${errors.idOrPassport ? "is-invalid" : ""}`}
                />
                {errors.idOrPassport && <div className="invalid-feedback">{errors.idOrPassport}</div>}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Phone Number</Form.Label>
                <Form.Control
                  type="text"
                  name="phoneNumber"
                  value={patientForm.phoneNumber}
                  placeholder="+267 7xxxxxxx"
                  onChange={handleValueChange}
                  className={`form-control ${errors.phoneNumber ? "is-invalid" : ""}`}
                />
                {errors.phoneNumber && <div className="invalid-feedback">{errors.phoneNumber}</div>}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Alternate Number</Form.Label>
                <Form.Control
                  type="text"
                  name="alternateNumber"
                  value={patientForm.alternateNumber}
                  onChange={handleValueChange}
                />
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={patientForm.email}
                  onChange={handleValueChange}
                  className={`form-control ${errors.email ? "is-invalid" : ""}`}
                />
                {errors.email && <div className="invalid-feedback">{errors.email}</div>}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Date of Birth</Form.Label>
                <Form.Control
                  type="date"
                  name="dob"
                  value={patientForm.dob}
                  onChange={handleValueChange}
                  className={`form-control ${errors.dob ? "is-invalid" : ""}`}
                />
                {errors.dob && <div className="invalid-feedback">{errors.dob}</div>}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Gender</Form.Label>
                <Form.Select
                  name="gender"
                  value={patientForm.gender}
                  onChange={handleValueChange}
                  className={`form-control ${errors.gender ? "is-invalid" : ""}`}
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </Form.Select>
                {errors.gender && <div className="invalid-feedback">{errors.gender}</div>}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Next of Kin</Form.Label>
                <Form.Control
                  type="text"
                  name="nextOfKin"
                  value={patientForm.nextOfKin}
                  onChange={handleValueChange}
                />
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Postal Address</Form.Label>
                <Form.Control
                  type="text"
                  name="postalAddress"
                  value={patientForm.postalAddress}
                  onChange={handleValueChange}
                />
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Physical Address</Form.Label>
                <Form.Control
                  type="text"
                  name="physicalAddress"
                  value={patientForm.physicalAddress}
                  onChange={handleValueChange}
                />
              </Form.Group>

              <div className="d-flex justify-content-end mt-4">
                <button type="submit" className="btn btn-submit me-2">
                  Add Patient
                </button>
                <button
                  type="button"
                  className="btn btn-cancel"
                  onClick={() => setPatientModal(false)}
                >
                  Cancel
                </button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      )}

      {/* Edit Patient Modal */}
      {showEditPatientModal && (
        <Modal show={showEditPatientModal} onHide={() => setShowEditPatientModal(false)} centered>
          <Modal.Header>
            <Modal.Title>Edit Patient</Modal.Title>
            <button
              type="button"
              className="btn-close me-1"
              onClick={() => setShowEditPatientModal(false)}
            >
              <X size={20} onClick={() => setShowEditPatientModal(false)} />
            </button>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handlePatientSubmit}>
              <Form.Group className="form-group">
                <Form.Label>Title</Form.Label>
                <Form.Select
                  name="title"
                  value={patientForm.title}
                  onChange={handleValueChange}
                  className={`form-control ${errors.title ? "is-invalid" : ""}`}
                >
                  <option value="">Select Title</option>
                  <option value="Mr">Mr</option>
                  <option value="Mrs">Mrs</option>
                  <option value="Miss">Miss</option>
                  <option value="Ms">Ms</option>
                </Form.Select>
                {errors.title && <div className="invalid-feedback">{errors.title}</div>}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type="text"
                  name="firstName"
                  value={patientForm.firstName}
                  onChange={handleValueChange}
                  className={`form-control ${errors.firstName ? "is-invalid" : ""}`}
                />
                {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Surname</Form.Label>
                <Form.Control
                  type="text"
                  name="surname"
                  value={patientForm.surname}
                  onChange={handleValueChange}
                  className={`form-control ${errors.surname ? "is-invalid" : ""}`}
                />
                {errors.surname && <div className="invalid-feedback">{errors.surname}</div>}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>ID/Passport</Form.Label>
                <Form.Control
                  type="text"
                  name="idOrPassport"
                  value={patientForm.idOrPassport}
                  onChange={handleValueChange}
                  className={`form-control ${errors.idOrPassport ? "is-invalid" : ""}`}
                />
                {errors.idOrPassport && <div className="invalid-feedback">{errors.idOrPassport}</div>}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Phone Number</Form.Label>
                <Form.Control
                  type="text"
                  name="phoneNumber"
                  value={patientForm.phoneNumber}
                  placeholder="+267 7xxxxxxx"
                  onChange={handleValueChange}
                  className={`form-control ${errors.phoneNumber ? "is-invalid" : ""}`}
                />
                {errors.phoneNumber && <div className="invalid-feedback">{errors.phoneNumber}</div>}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Alternate Number</Form.Label>
                <Form.Control
                  type="text"
                  name="alternateNumber"
                  value={patientForm.alternateNumber}
                  onChange={handleValueChange}
                />
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={patientForm.email}
                  onChange={handleValueChange}
                  className={`form-control ${errors.email ? "is-invalid" : ""}`}
                />
                {errors.email && <div className="invalid-feedback">{errors.email}</div>}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Date of Birth</Form.Label>
                <Form.Control
                  type="date"
                  name="dob"
                  value={patientForm.dob}
                  onChange={handleValueChange}
                  className={`form-control ${errors.dob ? "is-invalid" : ""}`}
                />
                {errors.dob && <div className="invalid-feedback">{errors.dob}</div>}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Gender</Form.Label>
                <Form.Select
                  name="gender"
                  value={patientForm.gender}
                  onChange={handleValueChange}
                  className={`form-control ${errors.gender ? "is-invalid" : ""}`}
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </Form.Select>
                {errors.gender && <div className="invalid-feedback">{errors.gender}</div>}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Next of Kin</Form.Label>
                <Form.Control
                  type="text"
                  name="nextOfKin"
                  value={patientForm.nextOfKin}
                  onChange={handleValueChange}
                />
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Postal Address</Form.Label>
                <Form.Control
                  type="text"
                  name="postalAddress"
                  value={patientForm.postalAddress}
                  onChange={handleValueChange}
                />
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Physical Address</Form.Label>
                <Form.Control
                  type="text"
                  name="physicalAddress"
                  value={patientForm.physicalAddress}
                  onChange={handleValueChange}
                />
              </Form.Group>

              <div className="d-flex justify-content-end mt-4">
                <button type="submit" className="btn btn-submit me-2">
                  {editPatientId ? "Update Patient" : "Add Patient"}
                </button>
                <button
                  type="button"
                  className="btn btn-cancel"
                  onClick={() => {
                    setPatientModal(false);
                    setShowEditPatientModal(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      )}
      {showDetailsModal && selectedPatient && (
        <Modal
          show={showDetailsModal}
          onHide={() => setShowDetailsModal(false)}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Patient Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-md-6">
                <div className="mb-3">
                  <strong>Title:</strong> {selectedPatient.title || '-'}
                </div>
                <div className="mb-3">
                  <strong>First Name:</strong> {selectedPatient.firstName}
                </div>
                <div className="mb-3">
                  <strong>Surname:</strong> {selectedPatient.surname || '-'}
                </div>
                <div className="mb-3">
                  <strong>ID/Passport:</strong> {selectedPatient.idOrPassport || '-'}
                </div>
                <div className="mb-3">
                  <strong>Phone Number:</strong> {selectedPatient.phoneNumber || '-'}
                </div>
                <div className="mb-3">
                  <strong>Alternate Number:</strong> {selectedPatient.alternateNumber || '-'}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <strong>Email:</strong> {selectedPatient.email || '-'}
                </div>
                <div className="mb-3">
                  <strong>Date of Birth:</strong> {selectedPatient.dob ?
                    new Date(selectedPatient.dob).toLocaleDateString() : '-'}
                </div>
                <div className="mb-3">
                  <strong>Gender:</strong> {selectedPatient.gender || '-'}
                </div>
                <div className="mb-3">
                  <strong>Next of Kin:</strong> {selectedPatient.nextOfKin || '-'}
                </div>
                <div className="mb-3">
                  <strong>Postal Address:</strong> {selectedPatient.postalAddress || '-'}
                </div>
                <div className="mb-3">
                  <strong>Physical Address:</strong> {selectedPatient.physicalAddress || '-'}
                </div>
              </div>
              <div className="col-12">
                <div className="mb-3">
                  <strong>Created At:</strong> {new Date(selectedPatient.createdAt).toLocaleString()}
                </div>
                <div className="mb-3">
                  <strong>Last Updated:</strong> {new Date(selectedPatient.updatedAt).toLocaleString()}
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-secondary"
              onClick={() => setShowDetailsModal(false)}
            >
              Close
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default PatientsCRUD;